import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";

import { zodResolver } from "@hookform/resolvers/zod";

import { Input, FormGroup } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import { Language } from "imports/Utils";

import LabelTypeSelect from "view/labels/components/selects/LabelTypeSelect";
import { labelSchema, LabelSchemaInput, LabelSchemaOutput } from "view/labels/utils/label.schema";

import { LabelType } from "__generated__/enums";
import { LabelFragment } from "__generated__/view/labels/graphql/fragments.generated";
import { SaveLabelMutation, SaveLabelDocument } from "__generated__/view/labels/graphql/mutations.generated";

const LabelCreateEditDialog = ({
	open,
	label,
	onClose,
	onSave,
}: {
	open: boolean;
	label: LabelFragment | null;
	onClose: () => void;
	onSave: () => void;
}) => {

	const [ saveLabel, { loading } ] = useMutation<SaveLabelMutation>(SaveLabelDocument);

	const defaultValues = {
		id: null,
		text: "",
		color: "#FFFFFF",
		type: LabelType.Comment,
	};

	const { control, handleSubmit, reset } = useForm<LabelSchemaInput, unknown, LabelSchemaOutput>({
		resolver: zodResolver(labelSchema),
		defaultValues: defaultValues,
	});

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
	};

	const save = (data: LabelSchemaOutput) => {
		saveLabel({
			variables: {
				input: {
					id: data.id ?? undefined,
					text: data.text,
					type: data.type,
					color: data.color,
				},
			},
		}).then(() => {
			handleOnClose();
			onSave();
		});
	};

	useEffect(() => {
		if (open && label) {
			reset(label);
		}
	}, [ open ]);

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "LABEL" })}
			form='labelForm'
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="labelForm">
				<FormGroup
					label={Language("LABEL_DESCRIPTION")}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="text"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} autoFocus />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				<FormGroup label={Language("COLOR")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="color"
						control={control}
						render={({ field }) => <Input type="color" {...field} />} />
				</FormGroup>

				<FormGroup label={Language("TYPE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="type"
						control={control}
						render={({ field }) => <LabelTypeSelect {...field} clearable={false} />} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default LabelCreateEditDialog;
